import * as Yup from 'yup';
import { endOfDay, isPast, isValid, subYears } from 'date-fns';
import { isUndefined } from 'lodash';
import { getArmenianLocalDate, getDiffInYearsFromNow } from '../../../helpers/dates';
import {
  ONCO_CARE_CHILD_MAX_AGE,
  ONCO_CARE_CHILD_MIN_AGE,
  ONCO_CARE_MAX_AGE,
  ONCO_CARE_MIN_AGE,
} from '../../../constants/oncoCare';
import {
  contractSignerSchema,
  englishStreetFieldValidations,
  mainUserFormSchema,
  passportValidator,
} from '../../userFormValidations';
import { isArmenianCountry } from '../../../helpers/countryHelper';
import { MATCHES_STRINGS_WITH_ZERO_OR_MORE_DIGITS, ONLY_DIGITS_REGEX_ALT } from '../../../constants/patterns';
import { getPassportsValidation } from '../../../helpers/armenianPassportsValidationHelper';
import { MIN_USER_AGE } from '../../../constants';

export const memberSchema = mainUserFormSchema.shape({
  socCard: Yup.string()
    .when(['citizenship'], (citizenship, schema) => {
      if (isArmenianCountry(citizenship)) {
        return schema
          .test('socialCard', 'validationErrors.socialCardWrongNumber', (value) => {
            if (value) {
              return !(
                (value.length !== 10)
                  || (!MATCHES_STRINGS_WITH_ZERO_OR_MORE_DIGITS.test(value))
              );
            }

            return true;
          }).required('validationErrors.isRequired');
      }
      return schema;
    }),
  passport: Yup.string()
    .when(['citizenship', 'birthday'], (citizenship, birthday, schema, test) => {
      if (isArmenianCountry(citizenship) && getDiffInYearsFromNow(birthday) > MIN_USER_AGE) {
        return getPassportsValidation(citizenship, schema, test);
      }
      return schema.required('validationErrors.isRequired').min(3, 'validationErrors.wrongPassportNumber');
    }),
  passportFrom: Yup.string()
    .when(['citizenship', 'birthday', 'isChild'], (citizenship, birthday, isChild, schema) => {
      if (isArmenianCountry(citizenship) && getDiffInYearsFromNow(birthday) >= MIN_USER_AGE) {
        return schema.test('passportFrom', 'validationErrors.wrongIssuer', (value) => ONLY_DIGITS_REGEX_ALT.test(value)).required('validationErrors.isRequired');
      }
      if (isArmenianCountry(citizenship) && getDiffInYearsFromNow(birthday) < MIN_USER_AGE) {
        return schema;
      }
      return schema.required('validationErrors.isRequired');
    }),
  passportGiven: Yup.date()
    .nullable()
    .when(['citizenship', 'birthday'], (citizenship, birthday, schema) => {
      if (isArmenianCountry(citizenship) && getDiffInYearsFromNow(birthday) >= MIN_USER_AGE && (!isUndefined(birthday) && birthday.toString() !== 'Invalid date')) {
        return schema.max(
          endOfDay(new Date()), 'validationErrors.passportGivenMessage',
        ).typeError('validationErrors.invalidDate').required('validationErrors.isRequired');
      }
      if (isArmenianCountry(citizenship) && getDiffInYearsFromNow(birthday) < MIN_USER_AGE && (!isUndefined(birthday) && birthday.toString() !== 'Invalid date')) {
        return schema;
      }
      return schema.required('validationErrors.isRequired');
    }).max(endOfDay(new Date()), 'validationErrors.passportGivenMessage')
    .typeError('validationErrors.invalidDate'),
  passportValidUntil: Yup.date()
    .nullable()
    .when(['passportGiven', 'birthday', 'citizenship'], (passportGiven, birthday, citizenship, schema) => {
      if (isValid(new Date(passportGiven))
          && getDiffInYearsFromNow(birthday) >= MIN_USER_AGE
          && isArmenianCountry(citizenship)
      ) {
        return schema.test(
          'validityDate',
          'validationErrors.validityDate',
          (value) => !value || (value > passportGiven && !isPast(value)),
        ).typeError('validationErrors.invalidDate').required('validationErrors.isRequired');
      }
      if (isValid(new Date(passportGiven))
          && getDiffInYearsFromNow(birthday) < MIN_USER_AGE
          && isArmenianCountry(citizenship)
      ) {
        return schema.test(
          'validityDate',
          'validationErrors.validityDate',
          (value) => !isPast(value),
        );
      }
      return schema.test(
        'validityDate',
        'validationErrors.validityDate',
        (value) => !isPast(value),
      ).required('validationErrors.isRequired');
    }).typeError('validationErrors.invalidDate'),
  birthday: Yup.date()
    .when(['isChild'], (isChild, schema) => {
      if (isChild === true) {
        return schema.min(subYears(new Date(), ONCO_CARE_CHILD_MAX_AGE), 'validationErrors.maxAge21');
      } if (isChild === false) {
        return schema
          .min(subYears(new Date(), ONCO_CARE_MAX_AGE), 'validationErrors.oncoCareMaxAge')
          .max(subYears(new Date(), MIN_USER_AGE), 'validationErrors.minAge');
      }
      return schema;
    })
    .max(getArmenianLocalDate(), 'validationErrors.wrongBirthday')
    .nullable()
    .typeError('validationErrors.invalidDate')
    .required('validationErrors.isRequired'),
}).concat(englishStreetFieldValidations);

export const newOnCoCareMemberSchema = memberSchema.concat(
  Yup.object().shape({
    passport: Yup.reach(memberSchema, 'passport').test(
      'duplicatePassport',
      'validationErrors.duplicatePassport',
      passportValidator.checkIfUnique,
    ),
  }),
);

export const oncoCareContractMembersSchema = Yup.object().shape({
  members: Yup.array()
    .of(memberSchema)
    .unique('duplicate passport', 'passport'),
});

export const oncoCarePricePayloadSchema = Yup.object({
  insuredPeople: Yup.array().of(
    Yup.object({
      age: Yup.number()
        .required('validationErrors.isRequired')
        .when(['isParent', 'isPerson', 'isChild'], {
          is: (isParent) => isParent === true,
          then: Yup.number().min(ONCO_CARE_MIN_AGE, 'validationErrors.oncoCareAge').max(ONCO_CARE_MAX_AGE, 'validationErrors.oncoCareAge'),
        })
        .when(['isPerson', 'isParent', 'isChild'], {
          is: (isPerson) => isPerson === true,
          then: Yup.number().min(ONCO_CARE_MIN_AGE, 'validationErrors.oncoCareAge').max(ONCO_CARE_MAX_AGE, 'validationErrors.oncoCareAge'),
        })
        .when(['isChild', 'isParent', 'isPerson'], {
          is: (isChild) => isChild === true,
          then: Yup.number().min(ONCO_CARE_CHILD_MIN_AGE, 'validationErrors.oncoCareAge').max(ONCO_CARE_CHILD_MAX_AGE, 'validationErrors.oncoCareAge'),
        }),

      riskAmount: Yup.number().required('Risk amount is required'),
      citizenship: Yup.string().required('Citizenship is required'),
      isParent: Yup.boolean(),
      isPerson: Yup.boolean(),
      isChild: Yup.boolean(),
    }),
  ),
});

export const calculatorSchema = oncoCarePricePayloadSchema.shape({
  age: Yup.number()
    .nullable()
    .min(ONCO_CARE_MIN_AGE, 'validationErrors.oncoCareAge')
    .max(ONCO_CARE_MAX_AGE, 'validationErrors.oncoCareAge')
    .required('validationErrors.isRequired'),
});

export const contractCalculatorSchema = calculatorSchema.shape({
  age: Yup.number().notRequired(),
  hasAgreed: Yup.boolean().oneOf([true]),
});

export const oncoCareContractSignerSchema = contractSignerSchema.shape({
  birthday: Yup.date()
    .max(subYears(new Date(), MIN_USER_AGE), 'validationErrors.minAge')
    .min(subYears(new Date(), ONCO_CARE_MAX_AGE), 'validationErrors.oncoCareMaxAge')
    .typeError('validationErrors.invalidDate')
    .nullable()
    .required('validationErrors.isRequired'),
});
